import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearError, setIncludeShipping } from 'software_renewal/redux/slice';
import { populateRenewal, checkCartItems } from 'software_renewal/redux/thunks';
import { doAddToCart } from 'shared/thunks';
import {
  dataForProductsToPurchase,
  selectLicenseInfo,
} from 'software_renewal/redux/selectors';

const ProductsToPurchase = () => {
  const licenseInfo = useSelector(selectLicenseInfo);
  const { products, totals } = useSelector(dataForProductsToPurchase);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  if (!products?.length) {
    return null;
  }

  const { displaySubtotal, displayTotal } = totals;

  const onIncludeShippingChange = (variantId, checked) => {
    dispatch(setIncludeShipping({ variantId, value: checked }));
  };

  const onRenewNow = async () => {
    setLoading(true);
    try {
      const cart = await dispatch(checkCartItems()).unwrap();
      const promises = [];

      products.forEach((product) => {
        if (product.isSoftwareRenewal) {
          promises.push(
            dispatch(
              populateRenewal({
                serial: licenseInfo.serialNumber,
                expirationDate: licenseInfo.expirationDate,
                reminderId: Spree?.reminder_id || undefined,
              }),
            ).unwrap(),
          );
        } else {
          const form = new FormData();
          form.append('variant_id', product.variant.id);
          form.append('quantity', 1);
          form.append('parameters[parent_serial]', licenseInfo.serialNumber);

          if (Spree?.reminder_id) {
            form.append('parameters[reminder_id]', Spree.reminder_id);
          }

          // Add parent product name for upgrades (Essentials or Professional)
          if (product.parentProductName) {
            form.append(
              'parameters[parent_product_name]',
              product.parentProductName,
            );
          }

          form.append(
            'parameters[discounted_price]',
            product.appliedPromotion.discounted_total,
          );
          if (product.appliedPromotion.prorated_discount > 0) {
            form.append(
              'parameters[prorated_discount]',
              product.appliedPromotion.prorated_discount,
            );
          }

          form.append(
            'parameters[expiration_date]',
            licenseInfo.expirationDate,
          );

          // Add include_shipping parameter if shouldIncludeShipping is defined
          if (product.shouldIncludeShipping !== undefined) {
            form.append(
              'parameters[include_shipping]',
              product.shouldIncludeShipping ? 1 : 0,
            );
          }

          promises.push(dispatch(doAddToCart(form)).unwrap());
        }
      });

      await Promise.all(promises);

      const { line_items, user_signed_in } = cart;
      if (line_items.length === 0 && user_signed_in) {
        // eslint-disable-next-line
        window.location.href = '/checkout';
      } else {
        // eslint-disable-next-line
        window.location.href = '/cart';
      }
    } catch (error) {
      // eslint-disable-next-line
      console.error('Error populating renewal:', error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        dispatch(clearError());
      }, 2000);
    }
  };

  return (
    <div className="card product-listing p-4">
      <table className="table product-listing border-0">
        <thead>
          <tr>
            <td className="border-0 title p-0" colSpan="4">
              Product to Purchase
              <hr />
            </td>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => {
            const deliveryMessageParts = [
              (product.isDigitalDelivery || product.shouldIncludeShipping) && 'Instant Digital Delivery',
              product.shouldIncludeShipping && 'You will be able to reuse your existing USB dongle.',
            ].filter(Boolean);

            const deliveryMessage = deliveryMessageParts.length
              ? deliveryMessageParts.join('. ') : null;

            return (
              <tr key={index}>
                <td className="border-0" colSpan={2}>
                  <div className="product-name">{product.fullProductName}</div>
                  <div>{product.optionValues.licenseType}</div>
                  <div>{product.optionValues.numberOfUsers}</div>
                  <div>{product.optionValues.licenseSpan}</div>
                  {deliveryMessage && (
                    <div className="instant-delivery mt-2">
                      {deliveryMessage}
                    </div>
                  )}
                  <div className="instant-delivery mt-2">
                    Technical Support and Access to Updates Included
                  </div>
                  {product.showIncludeShippingCheckbox && (
                    <div className="mt-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="mb-0 d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={Boolean(product.shouldIncludeShipping)}
                          onChange={(event) =>
                            onIncludeShippingChange(product.variant.id, event.target.checked)
                          }
                        />
                        I already have a USB Dongle.
                      </label>
                    </div>
                  )}
                </td>
                <td className="border-0" />
                <td className="border-0 product-price text-right">
                  {product.displayPrice}
                </td>
              </tr>
            );
          })}
          <tr>
            <td />
            <th colSpan={2} className="text-right">
              Subtotal
            </th>
            <td className="text-right">{displaySubtotal}</td>
          </tr>
          {totals.discounts.map((discount, index) => (
            <tr key={index}>
              <td className="border-0" />
              <th colSpan={2} className="border-0 text-right">
                {discount.promotionName}{' '}
                {discount.discountPercentage
                  ? `(${discount.discountPercentage}%)`
                  : ''}
              </th>
              <td className="product-price border-0 text-right">
                -{discount.displayDiscount}
              </td>
            </tr>
          ))}
          <tr>
            <td className="border-0" />
            <td />
            <td />
            <td
              className="product-price text-right"
              style={{ fontSize: '24px' }}
            >
              {displayTotal}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="d-flex justify-content-end">
        <button
          disabled={loading}
          type="button"
          onClick={onRenewNow}
          className="btn btn-primary text-nowrap"
        >
          Purchase Now
        </button>
      </div>
    </div>
  );
};

export default ProductsToPurchase;
